import React, { useState, useEffect } from 'react';
import { useSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Autocomplete, Box, CircularProgress, Link, Typography, Tooltip } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { AttachementsUpload } from '../../../../shared_elements/components';
import { checkApiStatus, numberWithCommas } from '../../../../utils';
import { backendDateFormat, fieldDateFormat } from '../../../../constants';
import { PageLoader } from '../../../../shared_elements';
import moment from 'moment';
import { globalPutService, globalPostService, globalGetService } from '../../../../utils/globalApiServices';
import { updateInsuranceApi, addInsuranceApi } from '../apiService';
import { getConstantTypesApi, getCurrencyAc } from '../../../../shared_elements/actionCreators';
import { setFileUploadDetails } from '../../../../shared_elements/actions';
import { trackActivity } from '../../../../utils/mixpanel';
import { formatBytes } from '../../../../utils/fileUpload';
import { regexConstants } from '../../../../constants/regEx';
import { getInsuranceTypeListApi } from '../../../settings/insurance_type/apiService';
import LessMoreTextFieldComp from '../../../../shared_elements/components/LessMoreTextFieldComp';
import InfoIcon from '@mui/icons-material/Info';


const InsuranceCRU = ({ insuranceCRU, modeType, fileUpload, getResponseBack, onOpenAttachments, onToggleModal, usedInsuranceTypeIds = [] }) => {
    const { leaseSlug } = useParams();
    const navigate=useNavigate();
    const dispatch = useDispatch()
    const { storageDetails } = useSelector((state) => state.leases); 
    const params = { leaseSlug: leaseSlug };
    const { enqueueSnackbar } = useSnackbar()
    const [error, setError] = useState({});
    const [isLoading, setLoading] = useState(false);
    const [data, setData] = useState(insuranceCRU?.data);
    const [attachments, setAttachments] = useState([]);
    const [cleared, setCleared] = useState(false);
    const { constantTypes, currencies } = useSelector((state) => state.leases);
    const [dropDownLoader,setDropDownLoader]=useState({
        insurance_type: false,
        currency: !currencies?.length,
        variability: !constantTypes?.filter(i => i?.type === 'variability')?.length,
    });
    const [insuranceType,setInsuranceType]=useState([]);

    useEffect(() => {
        dispatch(getConstantTypesApi({ constant_types: ["variability"] }));
        dispatch(getCurrencyAc({ constant_types: ["currency"] }));
        getInsuranceTypeList();
        if (cleared) {
            const timeout = setTimeout(() => {
                setCleared(false);
            }, 1500);
            return () => clearTimeout(timeout);
        }
        return () => { };
    }, [])

    const getInsuranceTypeList=()=>{
        setDropDownLoader(prev => ({ ...prev, insurance_type: true, currency: true, variability: true }));
        globalGetService(getInsuranceTypeListApi()).then(response=>{
            if(checkApiStatus(response)){
                setInsuranceType(response?.data?.data?.list);
            }
            setDropDownLoader(prev => ({ ...prev, insurance_type: false, currency: false, variability: false }));
        })
    }

    const selectedInsuranceTypeId = Number(data?.insurance_id?.id ?? data?.insurance_id ?? data?.insurance_type?.id);
    const filteredInsuranceTypeOptions = (insuranceType || []).filter((option) => {
        const optionId = Number(option?.id);
        if (Number.isNaN(optionId)) return false;
        const isCurrentSelection = optionId === selectedInsuranceTypeId;
        const isUsed = (usedInsuranceTypeIds || []).some((id) => Number(id) === optionId);
        return isCurrentSelection || !isUsed;
    });
    const selectedInsuranceType = (insuranceType || [])?.find((option) => Number(option?.id) === selectedInsuranceTypeId) || (data?.insurance_type?.id ? data?.insurance_type : null);

    const handleFileChange = (files) => {
        setAttachments(files);
        dispatch(setFileUploadDetails({attachments:files}))
    };

    const onFieldChange = (key, value) => {
        setData({ ...data, [key]: value });
        setError((prev) => ({ ...prev, [key]: '' }));
    };

    const onRestErrorKey = (keyParam) => {
        setError({ ...error, [keyParam]: '' })
    }


    const onAddEditInsurance = () => {
        let validationInputs = {};
        validationInputs = {
            insurance_id: data?.insurance_id ? "" : "Please select Insurance Type",
            insurance_number: data?.insurance_number?.trim() ? "" : "Please enter Insurance Number",
            insurer: data?.insurer?.trim() ? "" : "Please enter Insurer",
            insuree: data?.insuree?.trim() ? "" : "Please enter Insuree",
            policy_start_date: data?.policy_start_date ? "" : "Please select Policy Start Date",
            policy_end_date: data?.policy_end_date ? "" : "Please select Policy End Date",
            currency: data?.currency ? "" : "Please select Currency ",
            effective_date: data?.effective_date ? "" : "Please select Effective Date",
        };
        const extEndDate = moment(data?.extension_end_date);
        const policyStartDate = moment(data?.policy_start_date);
        const policyEndDate = moment(data?.policy_end_date);
        const policyRenewalDate = moment(data?.policy_renewal_date);
        const effectiveDate = moment(data?.effective_date);
        
        if (data?.is_extension_applicable === true && data?.notice_period_start_date && extEndDate.isBefore(moment(data?.notice_period_start_date))) {
            validationInputs.extension_end_date = "Extension End Date should not be Before than Notice Period Start Date";
        }
        if(data?.policy_start_date && !policyStartDate.isValid()) {
            validationInputs.policy_start_date = "Please enter valid Policy Start Date";
        }
        // if (data?.policy_start_date && policyStartDate.isAfter(moment(), 'day')) {
        //     validationInputs.policy_start_date = "Policy Start Date cannot be in the future";
        // }
        if(data?.policy_end_date && !policyEndDate.isValid()) {
            validationInputs.policy_end_date = "Please enter valid Policy End Date"
        }
        if(data?.policy_renewal_date && !policyRenewalDate.isValid()) {
            validationInputs.policy_renewal_date = "Please enter valid Policy Renewal Date"
        }
        if(data?.effective_date && !effectiveDate.isValid()) {
            validationInputs.effective_date = "Please enter valid Effective Date"
        }

        let payload = {
            ...data,
            insurance_amount: data?.insurance_amount ? data?.insurance_amount : null
        }
        if (Object.keys(validationInputs).every((k) => { return validationInputs[k] === ""; })) {
            setLoading(true);
            if (data?.id) {
                globalPutService(updateInsuranceApi({ ...params, slug: data?.slug }), {...payload,variability: Object.keys(payload?.variability || {}).length === 0 ? null : payload?.variability,deductible_amount:payload?.deductible_amount==="" ? null :payload?.deductible_amount}).then(async (response) => {
                    if (checkApiStatus(response)) {
                        if (attachments?.length) {
                            fileUpload(data?.slug,'updated')
                        }
                        enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { horizontal: "right", vertical: "top" } });
                        onToggleModal()
                        getResponseBack();
                        setLoading(false);
                        trackActivity('Item Edited', { page_title: `Edit Insurance`, success_msg: response?.data?.message, item_id: data?.id, payload: data, response: response?.data })
                    } else {
                        setLoading(false);
                        enqueueSnackbar(response.data.message, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" }, });
                        trackActivity('Item Edited', { page_title: `Edit Insurance`, error_msg: response?.data?.message, item_id: data?.id, payload: data, response: response?.data })
                    }
                    setLoading(false);
                });
            } else {
                globalPostService(addInsuranceApi(params), {...payload,variability: Object.keys(payload?.variability || {}).length === 0 ? null : payload?.variability,deductible_amount:payload?.deductible_amount==="" ? null :payload?.deductible_amount}).then(async (response) => {
                    if (checkApiStatus(response)) {
                        if (attachments?.length) {
                            fileUpload(response?.data?.data?.slug,'created')
                        }
                        enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { horizontal: "right", vertical: "top" }, });
                        onToggleModal()
                        getResponseBack();
                        setLoading(false);
                        trackActivity('Item Added', { page_title: `Add Insurance`, success_msg: response?.data?.message, item_id: response?.data?.data?.id, payload: data, response: response?.data })
                    } else {
                        setLoading(false);
                        enqueueSnackbar(response?.data?.message, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" }, });
                        trackActivity('Item Added', { page_title: `Add Insurance`, error_msg: response?.data?.message, item_id: null, payload: data, response: response?.data })
                    }
                });
            }
        } else {
            setError(validationInputs);
            trackActivity(`Item ${modeType === 'add' ? 'Added' : 'Edited'}`, { page_title: `${modeType === 'add' ? 'Add' : modeType === 'edit' ? 'Edit' : 'View'} Insurance`, error_msg: validationInputs, })
        }
    };
    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: `${modeType === 'add' ? 'Add' : modeType === 'edit' ? 'Edit' : 'View'} Insurance`, page_type: page_type, })
    }

    return (
        <>
            <Dialog
                aria-labelledby="customized-dialog-title"
                className='add-lease-dialog'
                maxWidth='md'
                fullWidth
                open={true}
            >
                <DialogTitle sx={{ m: 0, p: 2 }} className='dialog-header'>
                    <Typography sx={{ marginLeft: '10px' }}>
                        {modeType === 'add' ? 'Add Insurance' : modeType === 'edit' ? 'Edit Insurance' : 'View Insurance'}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <Autocomplete
                                disablePortal
                                disableClearable
                                options={filteredInsuranceTypeOptions}
                                getOptionLabel={(option) => option?.name || ""}
                                noOptionsText={dropDownLoader?.insurance_type ? "Loading..." : <Link onClick={() => navigate(`/settings/insurance-type`)}  sx={{ textDecoration: "none", cursor: "pointer" }} >+ Add New Insurance Type</Link>}
                                disabled={modeType === 'view'}
                                value={selectedInsuranceType}
                                onChange={(e, value) => {
                                    setData((prev) => ({ ...prev, insurance_id: value?.id || null, insurance_type: value || null }));
                                    setError((prev) => ({ ...prev, insurance_id: '' }));
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Insurance Type"
                                        margin='normal'
                                        size='small'
                                        required
                                        error={!!error.insurance_id}
                                        helperText={error.insurance_id}
                                        disabled={modeType === 'view'}
                                        onFocus={() => onRestErrorKey("insurance_id")}
                                        slotProps={{
                                            inputLabel: { shrink: true },
                                            input: { ...params?.InputProps, endAdornment: dropDownLoader?.insurance_type ? (<CircularProgress color="inherit" size={20} />) : (params.InputProps.endAdornment), },
                                        }}

                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <TextField
                                required
                                id="insurance_number"
                                label="Insurance Number"
                                variant="outlined"
                                size='small'
                                margin='normal'
                                error={!!error.insurance_number}
                                helperText={error.insurance_number}
                                slotProps={{ inputLabel: { shrink: true } }}
                                fullWidth
                                value={data?.insurance_number || ''}
                                disabled={modeType === 'view'}
                                onFocus={() => onRestErrorKey("insurance_number")}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    return regexConstants.restrictUnicodeCharacters.test(value) || value === "" ? value?.length <= 50 ? onFieldChange("insurance_number", value) : e.preventDefault() : null
                                }}
                            />
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <TextField
                                required
                                id="insurer"
                                label="Insurer"
                                variant="outlined"
                                size='small'
                                margin='normal'
                                slotProps={{ inputLabel: { shrink: true } }}
                                fullWidth
                                error={!!error.insurer}
                                helperText={error.insurer}
                                value={data?.insurer || ''}
                                disabled={modeType === 'view'}
                                onFocus={() => onRestErrorKey("insurer")}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    return regexConstants.restrictUnicodeCharacters.test(value) || value === "" ? value?.length <= 255 ? onFieldChange("insurer", value) : e.preventDefault() : null
                                }}
                            />
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <TextField
                                required
                                id="insuree"
                                label="Insuree"
                                variant="outlined"
                                size='small'
                                margin='normal'
                                slotProps={{ inputLabel: { shrink: true } }}
                                fullWidth
                                error={!!error.insuree}
                                helperText={error.insuree}
                                value={data?.insuree || ''}
                                onFocus={() => onRestErrorKey("insuree")}
                                disabled={modeType === 'view'}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    return regexConstants.restrictUnicodeCharacters.test(value) || value === "" ? value?.length <= 255 ? onFieldChange("insuree", value) : e.preventDefault() : null
                                }}
                            />
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label="Policy Start Date"
                                    value={data?.policy_start_date ? moment(data.policy_start_date) : null}
                                    onChange={(data) => { onFieldChange("policy_start_date", data ? moment(data).format(backendDateFormat) : null) }}
                                    slotProps={{
                                        textField: {
                                            required: true,
                                            InputLabelProps: { shrink: true },
                                            size: "small",
                                            margin: "normal",
                                            error: error.policy_start_date || false,
                                            helperText: error.policy_start_date || '',
                                            onFocus: () => { error.policy_start_date && onRestErrorKey("policy_start_date"); }
                                        },
                                        field: {
                                            clearable: true,
                                            onClear: () => {
                                                onFieldChange("policy_start_date", null);
                                                setCleared(true);
                                            }
                                        },
                                    }}
                                    sx={{ width: "100%" }}
                                    format={fieldDateFormat}
                                    disabled={modeType === 'view'}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label="Policy End Date"
                                    value={data?.policy_end_date ? moment(data.policy_end_date) : null}
                                    // onChange={(data) => onFieldChange("policy_end_date", moment(data)?.isValid() ? moment(data).format(backendDateFormat) : null)}
                                    onChange={(data) => { onFieldChange("policy_end_date", data ? moment(data).format(backendDateFormat) : null) }}
                                    minDate={data?.policy_start_date ? moment(data.policy_start_date) : null}
                                    slotProps={{
                                        textField: {
                                            required: true,
                                            InputLabelProps: { shrink: true },
                                            size: "small",
                                            margin: "normal",
                                            error: error.policy_end_date || false,
                                            helperText: error.policy_end_date || '',
                                            onFocus: () => { error.policy_end_date && onRestErrorKey("policy_end_date"); }
                                        },
                                        field: {
                                            clearable: true,
                                            onClear: () => {
                                                onFieldChange("policy_end_date", null);
                                                setCleared(true);
                                            }
                                        },
                                    }}
                                    sx={{ width: "100%" }}
                                    format={fieldDateFormat}
                                    disabled={modeType === 'view'}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label="Policy Renewal Date"
                                    value={data?.policy_renewal_date ? moment(data.policy_renewal_date) : null}
                                    // onChange={(data) => onFieldChange("policy_renewal_date", moment(data)?.isValid() ? moment(data).format(backendDateFormat) : null)}
                                    onChange={(data) => { onFieldChange("policy_renewal_date", data ? moment(data).format(backendDateFormat) : null) }}
                                    slotProps={{
                                        textField: {
                                            InputLabelProps: { shrink: true },
                                            size: "small",
                                            margin: "normal",
                                            error: error.policy_renewal_date || false,
                                            helperText: error.policy_renewal_date || '',
                                            onFocus: () => { error.policy_renewal_date && onRestErrorKey("policy_renewal_date"); } },
                                        field: {
                                            clearable: true,
                                            onClear: () => {
                                                onFieldChange("policy_renewal_date", null);
                                                setCleared(true);
                                            }
                                        },
                                    }}
                                    sx={{ width: "100%" }}
                                    format={fieldDateFormat}
                                    disabled={modeType === 'view'}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <TextField
                                id="re_insurer"
                                label="Re-Insurer"
                                variant="outlined"
                                size='small'
                                margin='normal'
                                slotProps={{ inputLabel: { shrink: true } }}
                                fullWidth
                                value={data?.re_insurer || ''}
                                disabled={modeType === 'view'}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    return regexConstants.restrictUnicodeCharacters.test(value) || value === "" ? value?.length <= 255 ? onFieldChange("re_insurer", value) : e.preventDefault() : null
                                }}
                            />
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <TextField
                                id="geo_limit"
                                label="Geo-Limit"
                                variant="outlined"
                                size='small'
                                margin='normal'
                                slotProps={{ inputLabel: { shrink: true } }}
                                fullWidth
                                value={data?.geo_limit || ''}
                                disabled={modeType === 'view'}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    return regexConstants.restrictUnicodeCharacters.test(value) || value === "" ? value?.length <= 255 ? onFieldChange("geo_limit", value) : e.preventDefault() : null
                                }}
                            />
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <Autocomplete
                                disablePortal
                                disableClearable
                                options={currencies || []}
                                getOptionLabel={(option) => option?.name || ""}
                                disabled={modeType === 'view'}
                                value={data?.currency || null}
                                onChange={(e, value) => { onFieldChange("currency", value) }}
                                noOptionsText={ modeType !== "view" && dropDownLoader?.currency ? "Loading..." : "No Option Available" }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Currency"
                                        margin='normal'
                                        size='small'
                                        required
                                        error={!!error.currency}
                                        helperText={error.currency}
                                        slotProps={{ inputLabel: { shrink: true }, input: { ...params?.InputProps, endAdornment: (<>{modeType !== "view" && dropDownLoader?.currency && ( <CircularProgress color="inherit" size={20} /> )} {params?.InputProps?.endAdornment} </>) } }}
                                        disabled={modeType === 'view'}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <TextField
                                // required
                                id="insurance_amount"
                                label={
                                    <span>
                                        Insurance Amount
                                        {data?.insurance_claims?.length > 0 ?
                                            <Tooltip placement='right' title={<span>Insurance Amount cannot be updated when a Claim exists </span>} arrow>
                                                <InfoIcon fontSize='small' color='primary' style={{ verticalAlign: 'middle', marginLeft: '2px', fontSize: 'medium' }} />
                                            </Tooltip> : null}
                                    </span>
                                }
                                variant="outlined"
                                size='small'
                                margin='normal'
                                slotProps={{ inputLabel: { shrink: true } }}
                                fullWidth
                                error={!!error.insurance_amount}
                                helperText={error.insurance_amount}
                                value={modeType === "view" && data?.insurance_amount !== '' && data?.insurance_amount !== null && data?.insurance_amount !== undefined ? numberWithCommas(data?.insurance_amount) : (data?.insurance_amount || "")}
                                disabled={modeType === 'view' || data?.insurance_claims?.length > 0}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === "") { onFieldChange("insurance_amount", ""); return; }
                                    if (val.length <= 20 && regexConstants.decimalnodotstart.test(val)) {
                                        onFieldChange("insurance_amount", val);
                                    }
                                }}
                            />
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <TextField
                                id="deductible_amount"
                                label="Deductible Amount"
                                variant="outlined"
                                size='small'
                                margin='normal'
                                slotProps={{ inputLabel: { shrink: true } }}
                                fullWidth
                                value={modeType === "view" && data?.deductible_amount !== '' && data?.deductible_amount !== null && data?.deductible_amount !== undefined ? numberWithCommas(data?.deductible_amount) : (data?.deductible_amount || "")}
                                disabled={modeType === 'view'}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === "") { onFieldChange("deductible_amount", ""); return; }
                                    if (val?.length <= 20 && regexConstants.decimalnodotstart.test(val)) {
                                        onFieldChange("deductible_amount", val);
                                    }
                                }}
                            />
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <LocalizationProvider dateAdapter={AdapterMoment}>
                                <DatePicker
                                    label="Effective Date"
                                    value={data?.effective_date ? moment(data.effective_date) : null}
                                    // onChange={(data) => onFieldChange("effective_date", moment(data)?.isValid() ? moment(data).format(backendDateFormat) : null)}
                                    onChange={(data) => {onFieldChange("effective_date", data ? moment(data).format(backendDateFormat) : null)}}
                                    slotProps={{
                                        textField: {
                                            required: true,
                                            InputLabelProps: { shrink: true },
                                            size: "small",
                                            margin: "normal",
                                            error: error.effective_date || false,
                                            helperText: error.effective_date || '',
                                            onFocus: () => { error.effective_date && onRestErrorKey("effective_date"); }
                                        },
                                        field: {
                                            clearable: true,
                                            onClear: () => {
                                                onFieldChange("effective_date", null);
                                                setCleared(true);
                                            }
                                        },
                                    }}
                                    sx={{ width: "100%" }}
                                    format={fieldDateFormat}
                                    disabled={modeType === 'view'}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <TextField
                                id="primary_contact"
                                label="Primary Contact"
                                variant="outlined"
                                size='small'
                                margin='normal'
                                slotProps={{ inputLabel: { shrink: true } }}
                                fullWidth
                                value={data?.primary_contact || ''}
                                disabled={modeType === 'view'}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    return regexConstants.restrictUnicodeCharacters.test(value) || value === "" ? value?.length <= 255 ? onFieldChange("primary_contact", value) : e.preventDefault() : null
                                }}
                            />
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <Autocomplete
                                disablePortal
                                options={(constantTypes && constantTypes?.filter(item => item?.type === 'variability')) || []}
                                getOptionLabel={(option) => option?.label || ""}
                                disabled={modeType === 'view'}
                                value={data?.variability || null}
                                onChange={(e, value) => { onFieldChange("variability", value) }}
                                noOptionsText={ modeType !== "view" && dropDownLoader?.variability ? "Loading..." : "No Option Available" }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Variability"
                                        margin='normal'
                                        size='small'
                                        disabled={modeType === 'view'}
                                        slotProps={{ inputLabel: { shrink: true }, input: { ...params?.InputProps, endAdornment: (<>{modeType !== "view" && dropDownLoader?.variability && ( <CircularProgress color="inherit" size={20} /> )} {params?.InputProps?.endAdornment} </>) } }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <TextField
                                id="lease_stipulated_requirement"
                                label="Lease Stipulated Requirement"
                                variant="outlined"
                                size='small'
                                margin='normal'
                                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 100 } }}
                                fullWidth
                                value={data?.lease_stipulated_requirement || ''}
                                onChange={(e) => {
                                    regexConstants.restrictUnicodeCharacters.test(e.target.value) || e.target.value === "" ? onFieldChange("lease_stipulated_requirement", e.target.value.trimStart()) : e.preventDefault()
                                    setError(prev => ({ ...prev, lease_stipulated_requirement: "" }));
                                }} 
                                disabled={modeType === 'view'}
                            />
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <TextField
                                id="aggregate"
                                label="Aggregate"
                                variant="outlined"
                                size='small'
                                margin='normal'
                                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 100 } }}
                                fullWidth
                                value={data?.aggregate || ''}
                                onChange={(e) => {
                                    regexConstants.restrictUnicodeCharacters.test(e.target.value) || e.target.value === "" ? onFieldChange("aggregate", e.target.value.trimStart()) : e.preventDefault()
                                    setError(prev => ({ ...prev, aggregate: "" }));
                                }} 
                                disabled={modeType === 'view'}
                            />
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <TextField
                                id="war"
                                label="War"
                                variant="outlined"
                                size='small'
                                margin='normal'
                                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 100 } }}
                                fullWidth
                                value={data?.war || ''}
                                onChange={(e) => {
                                    regexConstants.restrictUnicodeCharacters.test(e.target.value) || e.target.value === "" ? onFieldChange("war", e.target.value.trimStart()) : e.preventDefault()
                                    setError(prev => ({ ...prev, war: "" }));
                                }} 
                                disabled={modeType === 'view'}
                            />
                        </Grid>
                        <Grid size={{ md: 4, xs: 12 }}>
                            <TextField
                                id="government_indemnity"
                                label="Government Indemnity"
                                variant="outlined"
                                size='small'
                                margin='normal'
                                slotProps={{ inputLabel: { shrink: true }, htmlInput: { maxLength: 100 } }}
                                fullWidth
                                value={data?.government_indemnity || ''}
                                onChange={(e) => {
                                    regexConstants.restrictUnicodeCharacters.test(e.target.value) || e.target.value === "" ? onFieldChange("government_indemnity", e.target.value.trimStart()) : e.preventDefault()
                                    setError(prev => ({ ...prev, government_indemnity: "" }));
                                }} 
                                disabled={modeType === 'view'}
                            />
                        </Grid>
                        {modeType === 'view' ?
                            <Grid item size={{ md: 12, xs: 12 }}>
                                <LessMoreTextFieldComp label='Remarks' value={data?.remarks || ""} />
                            </Grid>
                            : <Grid size={{ md: 12, xs: 12 }}>
                                <TextField
                                    id="remarks"
                                    label="Remarks"
                                    variant="outlined"
                                    size='small'
                                    margin='normal'
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    sx={{ '& .MuiInputBase-input': { resize: 'vertical', overflow: 'auto' } }}
                                    fullWidth
                                    multiline
                                    rows={3}
                                    value={data?.remarks || ''}
                                    onChange={(e) => {
                                        regexConstants.restrictUnicodeCharacters.test(e.target.value) || e.target.value === "" ? onFieldChange("remarks", e.target.value.trimStart()) : e.preventDefault()
                                        setError(prev => ({ ...prev, remarks: "" }));
                                    }} 
                                    disabled={modeType === 'view'}
                                />
                            </Grid>}
                        <Grid size={{ md: 12, xs: 12 }} sx={{ mt: 3 }}>
                             <Box sx={{display:"flex",justifyContent:"space-between"}} >
                            {data?.id && data?.attachments?.length ?
                                <Button onClick={() => { onOpenAttachments(); handleItemClicked('ViewAttachments Button') }} size='small' variant='outlined' color='primary' sx={{ marginBottom: '10px' }}>({insuranceCRU?.data?.attachments?.length})Attachment(s)</Button> : null
                            }
                              <Button className='available-space-btn' disableRipple size='small' variant='outlined' color='primary'>{`Available Space : ${formatBytes(storageDetails?.remaining_storage)}`}</Button>
                          </Box>
                            {modeType === 'view' ? null : <AttachementsUpload onFileChange={handleFileChange} attachments={data?.attachments} remainingStorage={storageDetails?.remaining_storage} />}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions className="dialog-action">
                    <Button className='cancel-btn' disabled={isLoading} variant='outlined' size='small' onClick={() => { onToggleModal(); handleItemClicked('Cancel Button') }}>{modeType === "view" ? "Close" : "Cancel"}</Button>
                    {modeType === "view" ? null : <Button variant='contained' size='small' className='btn-primary' onClick={() => { handleItemClicked('Save Button'); onAddEditInsurance(); }}>{isLoading ? <CircularProgress color='inherit' size={25} /> : "SAVE"}</Button> }
                </DialogActions>
            </Dialog>
            {isLoading ? <PageLoader /> : null}
        </>

    )
}
export default InsuranceCRU
